import { Buffer } from 'buffer';
import _ from 'lodash';
import moment from 'moment';

import { config } from 'src/config/config';
import { Http } from 'src/shared/services/Http';
import { NavigationService } from 'src/shared/services/NavigationService';
import { ToastNotificationsService } from 'src/shared/services/ToastNotificationsService';
import { analyticsClient, EventType } from 'src/shared/services/analytics';

import { AuthData, Credentials, JWTData, PasswordGrant, RefreshGrant } from '../models/auth';

export class AuthenticationService {
    private static authRealm: string = config.defaultAuthRealm as string;
    private static AUTH_KEY: string = 'auth';

    private storage: Storage = localStorage;

    constructor(
        private http: Http,
        private toastNotificationsService: ToastNotificationsService,
        private navigationService: NavigationService,
    ) {}

    private get tokenUrl(): string {
        return AuthenticationService.authRealm + config.tokenPath;
    }

    public saveAuthData(authData: AuthData): void {
        this.storage.setItem(AuthenticationService.AUTH_KEY, JSON.stringify(authData));
    }

    public get authData(): AuthData | undefined {
        const data = this.storage.getItem(AuthenticationService.AUTH_KEY);

        return data && JSON.parse(data);
    }

    public jwtData(): JWTData | undefined {
        try {
            const tokens = this.authData;

            if (!tokens || !tokens['access_token']) {
                return undefined;
            }

            const decoded = Buffer.from(tokens['access_token'].split('.')[1], 'base64').toString(
                'ascii',
            );
            return JSON.parse(decoded);
        } catch {
            return undefined;
        }
    }

    public async logout(): Promise<void> {
        // so user will be taken to the dashboard after next login
        localStorage.removeItem('requestedUrl');
        localStorage.removeItem('requestedUrlParams');

        const authData = this.authData;
        this.storage.removeItem(AuthenticationService.AUTH_KEY);

        // Let the back-end know to revoke the refresh token
        try {
            await this.http.makeUnAuthenticatedRequest<AuthData>('DELETE', this.tokenUrl, authData);
        } catch {
            // Do nothing, but catch the rejected promise
        }
    }

    // also used to validate if provided password is correct in the `change password` process
    public async login(credentials: Credentials): Promise<void> {
        const data: PasswordGrant = {
            grant_type: 'password',
            scope: 'webapp',
            username: credentials.email,
            password: credentials.password,
        };

        try {
            const authData = await this.http.makeUnAuthenticatedRequest<PasswordGrant, AuthData>(
                'POST',
                this.tokenUrl,
                data,
            );

            this.saveAuthData(authData);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMessage = error?.body?.error || '';
            const accountExpired = errorMessage.includes('Your account has expired');

            analyticsClient.track('Login: Wrong Credentials', EventType.API, {
                page_name: 'LoginForm',
                status_code: error?.code,
                error_message: errorMessage,
            });

            if (accountExpired) {
                this.navigationService.navigate('/ineligible');

                return Promise.reject('');
            }

            switch (error.code) {
                // Unauthenticated, i.e. an Authorization header is not provided.
                case 401: {
                    return Promise.reject(config.messages.incorrectCredentials);
                }
                // Forbidden, i.e. the user doesn't have access to login.
                case 403: {
                    return Promise.reject(config.messages.incorrectCredentials);
                }
                // Too many requests, i.e. they've incorrectly logged in too many times.
                case 429:
                    return Promise.reject(error.body.error);
                default:
                    return Promise.reject(config.messages.connectionProblemError);
            }
        }
    }

    public isTokenValid(tokenType: string, authData?: AuthData): boolean {
        authData = authData || this.authData;

        if (!authData || !authData[tokenType]) {
            return false;
        }

        let payload;
        try {
            const decoded = Buffer.from(authData[tokenType].split('.')[1], 'base64').toString(
                'ascii',
            );
            payload = JSON.parse(decoded);
        } catch {
            return false;
        }

        if (!payload.exp) {
            return false;
        }

        const expiryDateTime = moment(payload.exp * 1000);
        const now = moment();
        const thirtyMinutesFromNow = now.clone().add(30, 'minutes');
        const unexpired = expiryDateTime > now;

        // if it's a refresh_token, just make sure it's unexpired.
        // However, if it's an access_token, we want to swap out the 30 day tokens for the 15 minute tokens.
        // So if it expires greater than 30 minute from now, we know it's an old 30 day token, and we should return
        // FALSE from this method (and thus the code will request a new token, which will be a 15 minute token).
        // See explanation here: https://joinmodernhealth.atlassian.net/browse/PRODUCT-957.
        // NOTE: using 30 minutes as the check just so we have extra leeway incase we want to bump this.
        // NOTE2: Remove this code once everyone's access token is definitely swapped? So 30 days after merge?
        return tokenType === 'access_token'
            ? unexpired && expiryDateTime < thirtyMinutesFromNow
            : unexpired;
    }

    // Perform an arbitrary HTTP request, adding auth headers transparently,
    // as well as catching unauthorized responses and handling them appropriately
    public async requestWithAuth(url: string, options: RequestInit): Promise<Response> {
        const authData = await this.refresh();

        options.headers = options.headers as Headers;
        if (authData !== null) {
            options.headers.append(
                'Authorization',
                `${authData.token_type} ${authData.access_token}`,
            );
        }

        return fetch(url, options).then(res => {
            if (res.status === 401 || res.status === 403) {
                // The auth realm tells us where to get our tokens from
                const authenticateHeader = res.headers.get('www-authenticate');
                if (authenticateHeader) {
                    AuthenticationService.authRealm = authenticateHeader.split('realm=')[1];
                }
            }
            return res;
        });
    }

    // Refresh the access token if needed
    public async refresh(skipClientValidation?: boolean): Promise<AuthData> {
        const authData = this.authData;

        if (!authData) {
            return Promise.reject('Auth data missing');
        }

        const accessTokenValid = skipClientValidation
            ? false
            : this.isTokenValid('access_token', authData);

        if (accessTokenValid) {
            return authData;
        }

        if (!authData.refresh_token || !this.isTokenValid('refresh_token', authData)) {
            return Promise.reject('Refresh token is invalid');
        }

        return this.refreshByToken(authData.refresh_token);
    }

    public async refreshByToken(refreshToken: string): Promise<AuthData> {
        const data: RefreshGrant = {
            grant_type: 'refresh_token',
            scope: 'webapp',
            refresh_token: refreshToken,
        };

        try {
            const newAuthData = await this.http.makeUnAuthenticatedRequest<RefreshGrant, AuthData>(
                'POST',
                this.tokenUrl,
                data,
            );

            this.saveAuthData({
                refresh_token: refreshToken,
                ...newAuthData,
            });

            return newAuthData;
        } catch (error) {
            const accountExpired = (_.get(error, 'body.error') || '').includes(
                'Your account has expired',
            );

            this.logout();

            if (accountExpired) {
                this.navigationService.navigate('/ineligible');
            } else {
                this.toastNotificationsService.renderWarningToast('Your session has expired.');
            }

            return Promise.reject();
        }
    }
}
